import { jwtToAuthUser, type AuthUser } from '@stayon/auth-core';
import type { StoreApi } from 'zustand';
import type { Api } from '@/shared/api';
import type { AuthState, AuthStatus } from './authState';
import { announceIdentityChange, identityKey } from './sessionIdentity';
import type { SupportSession } from './support/supportSession';

/** `hold`: die Identität des Tabs ist noch offen; `restoring`: der Tab stellt nur wieder her. */
export interface IdentityGate {
  hold: 'refreshing' | 'reconnecting' | null;
  restoring: boolean;
}

/** Gleiches Token, gleiches Objekt: sonst liefen die Router-Guards bei jeder Neuberechnung. */
export function memoizedUser(): (token: string | null | undefined) => AuthUser | null {
  let lastToken: string | null | undefined;
  let lastUser: AuthUser | null = null;
  return (token) => {
    if (token !== lastToken) {
      lastToken = token;
      lastUser = token ? jwtToAuthUser(token) : null;
    }
    return lastUser;
  };
}

/** Eine Api je Identität, solange sie gilt: Abfragen behalten ihre Instanz, bis die Identität wechselt. */
export function memoizedApi(build: (identity: string) => Api): (identity: string) => Api {
  let last: { identity: string; api: Api } | null = null;
  return (identity) => {
    if (last?.identity !== identity) last = { identity, api: build(identity) };
    return last.api;
  };
}

export const homeKeyOf = (user: AuthUser | null) =>
  user?.sessionFamilyId ? `${user.userId}|${user.tenantId}|${user.sessionFamilyId}` : null;

/**
 * Bestimmt die wirksame Identität aus Heimat- und Support-Sitzung. Ein Wechsel meldet sich über
 * `onIdentityChange`, bevor der Store ihn zeigt: so ist der Cache leer, ehe React neu rendert.
 * Mit der Identität wechselt die Api; was die vorige angestoßen hat, schickt sie nicht mehr.
 */
export function createIdentityPublisher({
  store,
  homeUser,
  support,
  gate,
  apiFor,
}: {
  store: () => StoreApi<AuthState> | null;
  homeUser: () => AuthUser | null;
  support: SupportSession;
  gate: IdentityGate;
  apiFor: (identity: string) => Api;
}): (status?: AuthStatus) => void {
  const supportUserOf = memoizedUser();
  return (status) => {
    const target = store();
    if (!target) return;
    const home = homeUser();
    if (!home) gate.hold = null;
    if (gate.hold) {
      target.setState({
        user: null,
        homeUser: home,
        support: null,
        status: gate.hold,
        api: apiFor(''),
      });
      return;
    }
    const view = home ? support.view() : null;
    const user = view ? supportUserOf(support.token()) : home;
    const previous = target.getState();
    const [from, to] = [identityKey(previous.user), identityKey(user)];
    if (from !== to) {
      announceIdentityChange({ previous: from, next: to, restored: from === '' && gate.restoring });
    }
    target.setState({
      user,
      homeUser: home,
      support: view,
      api: apiFor(to),
      status: status ?? (user ? previous.status : 'unauthenticated'),
    });
  };
}
